import { useEffect, useState } from "react";
import { MessageSquare, Pin, PinOff, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "../providers/supabase/supabase";

/** Mesmo número do trigger da migration 205. */
export const LIMITE_CONVERSAS = 30;
/** Mesmo teto do trigger da migration 223. */
export const LIMITE_FIXADAS = 10;

export type ConversaResumo = { id: string; title: string | null; last_message_at: string; fixada_em?: string | null };

/** Fixadas em cima (a fixada mais recente primeiro), depois pela última mensagem. */
export const ordenarConversas = (cs: ConversaResumo[]): ConversaResumo[] =>
  [...cs].sort((a, b) => {
    if (a.fixada_em && b.fixada_em) return b.fixada_em.localeCompare(a.fixada_em);
    if (a.fixada_em) return -1;
    if (b.fixada_em) return 1;
    return b.last_message_at.localeCompare(a.last_message_at);
  });

// fixada_em (migration 223) ainda não está em database.generated.ts.
const sb = supabase as unknown as { from: (t: string) => any; auth: typeof supabase.auth };

/** "hoje 14:05", "ontem", "12/09" — o suficiente pra achar a conversa. */
export const quandoFoi = (iso: string, agora = new Date()): string => {
  const d = new Date(iso);
  const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((dia(agora) - dia(d)) / 86_400_000);
  if (dias === 0) return `hoje ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  if (dias === 1) return "ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

/**
 * As conversas da pessoa com a North IA, da mais recente para a mais antiga,
 * com as fixadas em cima. O banco guarda até 30; fixada não sai (até 10).
 *
 * Filtro por user_id OBRIGATÓRIO: desde a migration 223 o super admin lê as
 * conversas de todos (para a Auditoria) — sem ele, a lista dele traria as dos
 * outros.
 *
 * `versao` muda quando a página manda uma mensagem — a conversa que acabou de
 * receber resposta sobe para o topo sem precisar reabrir o painel.
 */
export const ConversasNorthIA = ({
  aberta,
  onAbertaChange,
  conversaAtual,
  onEscolher,
  onNova,
  onApagada,
  versao,
}: {
  aberta: boolean;
  onAbertaChange: (aberta: boolean) => void;
  conversaAtual: string | undefined;
  onEscolher: (id: string) => void;
  onNova: () => void;
  onApagada: (id: string) => void;
  versao: number;
}) => {
  const [conversas, setConversas] = useState<ConversaResumo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [apagando, setApagando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    if (!aberta) return;
    let ativo = true;
    setCarregando(true);
    (async () => {
      const { data: sessao } = await sb.auth.getSession();
      const eu = sessao.session?.user.id;
      if (!eu) {
        if (ativo) setCarregando(false);
        return;
      }
      const buscar = (campos: string) =>
        sb
          .from("chat_conversations")
          .select(campos)
          .eq("user_id", eu)
          .order("last_message_at", { ascending: false })
          .limit(LIMITE_CONVERSAS);
      const comFixada = await buscar("id, title, last_message_at, fixada_em");
      // Banco sem a migration 223 (site publicado antes): lista sem alfinete
      // em vez de lista vazia.
      const { data } = comFixada.error ? await buscar("id, title, last_message_at") : comFixada;
      if (!ativo) return;
      setConversas(ordenarConversas((data ?? []) as ConversaResumo[]));
      setCarregando(false);
    })();
    return () => {
      ativo = false;
    };
  }, [aberta, versao]);

  const fixar = async (c: ConversaResumo) => {
    const fixada_em = c.fixada_em ? null : new Date().toISOString();
    if (fixada_em && conversas.filter((x) => x.fixada_em).length >= LIMITE_FIXADAS) {
      setAviso(`Limite de ${LIMITE_FIXADAS} conversas fixadas. Desafixe uma para continuar.`);
      return;
    }
    const { error } = await sb.from("chat_conversations").update({ fixada_em }).eq("id", c.id);
    if (error) {
      setAviso(error.message?.includes("Limite") ? error.message : "Não foi possível fixar a conversa.");
      return;
    }
    setAviso(null);
    setConversas((cs) => ordenarConversas(cs.map((x) => (x.id === c.id ? { ...x, fixada_em } : x))));
  };

  const apagar = async (id: string) => {
    setApagando(id);
    const { error } = await supabase.from("chat_conversations").delete().eq("id", id);
    setApagando(null);
    if (error) return;
    setConversas((cs) => cs.filter((c) => c.id !== id));
    onApagada(id);
  };

  return (
    <Sheet open={aberta} onOpenChange={onAbertaChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm">
        <SheetHeader>
          <SheetTitle>Conversas</SheetTitle>
          <SheetDescription>
            Até {LIMITE_CONVERSAS} ficam guardadas; a mais antiga sai quando nasce uma nova. Fixe
            (até {LIMITE_FIXADAS}) as que não podem sair.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              onNova();
              onAbertaChange(false);
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" /> Nova conversa
          </Button>
        </div>
        {aviso && <p className="mx-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">{aviso}</p>}
        <ul className="flex-1 space-y-1 overflow-y-auto px-4 pb-4">
          {carregando && conversas.length === 0 && (
            <li className="py-2 text-sm text-muted-foreground">Carregando...</li>
          )}
          {!carregando && conversas.length === 0 && (
            <li className="py-2 text-sm text-muted-foreground">Nenhuma conversa ainda.</li>
          )}
          {conversas.map((c) => (
            <li key={c.id} className="group flex items-center gap-1">
              <button
                type="button"
                aria-current={c.id === conversaAtual ? "true" : undefined}
                className={`flex min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted ${
                  c.id === conversaAtual ? "bg-muted font-medium" : ""
                }`}
                onClick={() => {
                  onEscolher(c.id);
                  onAbertaChange(false);
                }}
              >
                {c.fixada_em ? (
                  <Pin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-label="Fixada" />
                ) : (
                  <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{c.title?.trim() || "Sem título"}</span>
                  <span className="block text-xs text-muted-foreground">{quandoFoi(c.last_message_at)}</span>
                </span>
              </button>
              <button
                type="button"
                aria-label={`${c.fixada_em ? "Desafixar" : "Fixar"} conversa ${c.title ?? ""}`.trim()}
                title={c.fixada_em ? "Desafixar" : "Fixar (fora do limite de 30 conversas)"}
                className={`rounded-md p-2 hover:bg-muted ${
                  c.fixada_em ? "text-amber-600" : "text-muted-foreground opacity-60 group-hover:opacity-100"
                }`}
                onClick={() => fixar(c)}
              >
                {c.fixada_em ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              </button>
              <button
                type="button"
                aria-label={`Apagar conversa ${c.title ?? ""}`.trim()}
                className="rounded-md p-2 text-muted-foreground opacity-60 hover:bg-muted hover:text-destructive group-hover:opacity-100"
                disabled={apagando === c.id}
                onClick={() => apagar(c.id)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
};

export default ConversasNorthIA;
